// import LogoutIcon from '@mui/icons-material/Logout';
import { AccountCircle, ContentCopy, Description } from "@mui/icons-material";
import {
  Beenhere as BeenhereIcon,
  Cached as CachedIcon,
  Check as CheckIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Payment as PaymentIcon,
  RestaurantMenu as RestaurantMenuIcon,
  ShoppingCart as ShoppingCartIcon
} from "@mui/icons-material";
import {
  Badge,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Button,
  CircularProgress,
  Drawer,
  FormGroup,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography
} from "@mui/material";
import axios from "axios";
import { CurrencyCode, PaymentOptions, encode } from "bysquare";
import { useEffect, useMemo, useRef, useState } from "react";
import QRCode from "react-qr-code";
import { SERVER_API_PREFIX } from "../constants";
import { theme } from "../theme";
import {
  TCustomer,
  THungerGame,
  TPartialUser,
  TPizza,
  TPizzaOrder,
  TUser
} from "../types/AllTypes";
import { customerToString, userToString } from "../utils";
import { MyButton } from "./MyButton";
import { MyError } from "./Notifications";
import { Pizza } from "./Pizza";

export const PizzaOrdering = ({
  username,
  customer = undefined,
  canOrder,
  hungerGame,
  isRestricted
}: {
  username: string;
  customer?: TCustomer;
  canOrder: boolean;
  hungerGame: THungerGame;
  isRestricted: boolean;
}) => {
  const [orderedPizzas, setOrderedPizzas] = useState<TPizza[]>([]);
  const [pizzaOrders, setPizzaOrders] = useState<TPizzaOrder[]>([]);
  const [hasOrdered, setHasOrdered] = useState(false);
  const [picked, setPicked] = useState(false);
  const [pizzas, setPizzas] = useState<TPizza[]>([]);
  const [countMin, setCountMin] = useState<string>("--");
  const [countSec, setCountSec] = useState<string>("--");
  const [orderValid, setOrderValid] = useState(true);
  const [cartOpen, setCartOpen] = useState(false);
  const [qrString, setQrString] = useState<string>("");
  const [paymentLink, setPaymentLink] = useState<string>("");
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const orderedPizzasRef = useRef(orderedPizzas);
  const [sendingOrder, setSendingOrder] = useState(false);

  const startTimer = () => {
    setInterval(() => {
      if (canOrder) {
        const timecompare =
          (hungerGame.deadline.getTime() - new Date().getTime()) / 1000;

        if (timecompare < 0) {
          setCountMin("--");
          setCountSec("--");
          canOrder = false;
          window.location.reload();
        }

        const minutes = Math.floor(Math.floor(timecompare / 60));
        const seconds = Math.floor(
          timecompare - Math.round(Math.floor(timecompare / 60) * 60)
        );

        setCountMin(minutes.toString());
        if (seconds < 10) {
          setCountSec("0" + seconds);
        } else {
          setCountSec(seconds.toString());
        }
      }
    }, 500);
  };
  const addPizza = (pizza: TPizza) => {
    setOrderedPizzas([...orderedPizzas, pizza]);
    setTotalPrice(totalPrice + pizza.price);
  };

  const removePizza = (index: number) => {
    setTotalPrice(totalPrice - orderedPizzas[index].price);
    const newOrderedPizzas = orderedPizzas.filter(
      (_pizza, idx) => idx !== index
    );
    setOrderedPizzas(newOrderedPizzas);
    if (!(newOrderedPizzas.length > 0)) {
      setCartOpen(false);
    }
  };

  const changeNote = (index: number, note: string) => {
    const orderedPizzasCopy = orderedPizzas.map((pizza, idx) => {
      if (idx === index) {
        return { ...pizza, note };
      }
      return pizza;
    });
    setOrderedPizzas(orderedPizzasCopy);
  };

  const fetchMenu = async () => {
    const response = await axios.get<TPizza[]>(`${SERVER_API_PREFIX}/menu`);
    const ordersResponse = await axios.get<TPizzaOrder[]>(
      `${SERVER_API_PREFIX}/order`,
      {
        params: {
          customer: customer ? customerToString(customer) : undefined
        }
      }
    );

    setPizzas(response.data);
    setOrderedPizzas(
      ordersResponse.data.flatMap(order =>
        Array.from({ length: order.count || 1 }, () => ({
          ...order.pizza,
          note: order.note
        }))
      )
    );
    setPizzaOrders(ordersResponse.data);
    const nTotalPrice = Number(
      ordersResponse.data
        .reduce((acc, order) => {
          return (order.count || 1) * order.pizza.price + acc;
        }, 0)
        .toFixed(2)
    );
    setTotalPrice(nTotalPrice);
    if (ordersResponse.data.length > 0) {
      setHasOrdered(true);
      if (hungerGame.iban) {
        const qs = encode({
          payments: [
            {
              type: PaymentOptions.PaymentOrder,
              amount: nTotalPrice,
              paymentNote: "PizzaHub platba - " + username,
              currencyCode: CurrencyCode.EUR,
              bankAccounts: [{ iban: hungerGame.iban }]
            }
          ]
        });
        if (hungerGame.creditorName)
          setPaymentLink(
            `https://payme.sk/2/p/PME?IBAN=${hungerGame.iban}&AM=${nTotalPrice.toFixed(2)}&CC=EUR&CN=${hungerGame.creditorName}&MSG=PizzaHub%20platba%20-%20${username}`
          );
        setQrString(qs);
      }
    }
    setPicked(ordersResponse.data.every(order => order.picked));
    startTimer();
    setOrderValid(ordersResponse.data.every(order => order.valid));
    setSendingOrder(false);
  };
  useEffect(() => {
    fetchMenu();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    orderedPizzasRef.current = orderedPizzas;
  }, [orderedPizzas]);

  const sendOrder = async () => {
    if (orderedPizzas.length > 0) {
      try {
        setSendingOrder(true);
        await axios.post(`${SERVER_API_PREFIX}/order`, {
          customer: customer ? customerToString(customer) : undefined,
          orderedPizzas: orderedPizzas.map(pizza => {
            return { pizzaId: pizza.id, note: pizza.note };
          })
        });
        // setHasOrdered(true);
        setTimeout(fetchMenu, 500);
      } catch (err) {
        console.log(err);
        alert("ou nou");
      }
    } else {
      setCartOpen(false);
      alert("Tvoj kosik je prazdny");
    }
  };

  const cancelOrder = async () => {
    if (window.confirm("Naozaj chces zrusit objednavku?")) {
      try {
        await axios.post(`${SERVER_API_PREFIX}/cancel-order`, {
          customer: customer ? customerToString(customer) : undefined
        });
        setHasOrdered(false);
        setCartOpen(false);
      } catch (err) {
        console.log(err);
        alert("objednavka sa nedala zrusit :(");
      }
    }
  };

  const isInOrderableList = (pizza: TPizza) => {
    return pizzas.filter(oPizza => oPizza.id === pizza.id).length > 0;
  };

  return (
    <>
      {sendingOrder && (
        <>
          <Box
            position="fixed"
            top={0}
            left={0}
            width="100%"
            height="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            bgcolor="rgba(0, 0, 0, 0.7)"
            zIndex={9999}
          >
            <Paper
              elevation={3}
              sx={{
                padding: 4,
                display: "flex",
                flexDirection: "column",
                alignItems: "center"
              }}
            >
              <CircularProgress size={60} color="primary" />
              <Typography variant="h6" sx={{ mt: 2 }}>
                Posielam objednavku...
              </Typography>
            </Paper>
          </Box>
        </>
      )}
      {!hasOrdered && canOrder ? (
        <Box paddingBottom={8}>
          <Grid
            container
            justifyContent={"center"}
            maxWidth={"1200px"}
            margin="0 auto"
          >
            {pizzas
              .toSorted((a: TPizza, b: TPizza) => {
                return a.position - b.position || a.name.localeCompare(b.name);
              })
              .map(pizza => {
                return (
                  <Pizza
                    pizza={pizza}
                    addPizza={addPizza}
                    key={pizza.id}
                  ></Pizza>
                );
              })}
          </Grid>
          <Box position={"fixed"} bottom={0} left={0} right={0} width={"100%"}>
            <BottomNavigation showLabels>
              <BottomNavigationAction
                disabled={orderedPizzas.length == 0}
                onClick={() => {
                  setCartOpen(true);
                }}
                label={`Kosik`}
                icon={
                  <Badge badgeContent={orderedPizzas.length} color="primary">
                    <ShoppingCartIcon />
                  </Badge>
                }
              />
            </BottomNavigation>
            {hungerGame && !hungerGame.finished && (
              <Box
                display={"flex"}
                bgcolor={theme.palette.background.default}
                // fullWidth
                justifyContent={"center"}
              >
                <Typography variant="h6" sx={{ textAlign: "center" }}>
                  {countMin} : {countSec}
                </Typography>
              </Box>
            )}
          </Box>
          <Drawer //cart
            anchor={"bottom"}
            open={cartOpen}
            onClose={() => {
              setCartOpen(false);
            }}
          >
            <Box height={"80vh"}>
              <Grid
                container
                justifyContent={"center"}
                paddingBottom={8}
                maxWidth="1200px"
                margin="0 auto"
              >
                {orderedPizzas
                  .toSorted((a: TPizza, b: TPizza) => {
                    return (
                      a.position - b.position || a.name.localeCompare(b.name)
                    );
                  })
                  .map((pizza, idx) => {
                    return (
                      <Pizza
                        key={`${pizza.id}-${idx}`}
                        textcolor={
                          isInOrderableList(pizza) ? "text.primary" : "red"
                        }
                        pizza={pizza}
                        removePizza={() => {
                          removePizza(idx);
                        }}
                        changeNote={note => {
                          changeNote(idx, note);
                        }}
                      ></Pizza>
                    );
                  })}
              </Grid>
              <Box position={"fixed"} bottom={20} width={"100%"} paddingX={2}>
                <MyButton fullWidth onClick={sendOrder} disabled={isRestricted}>
                  <Typography
                    sx={{
                      fontWeight: 700
                    }}
                    variant="h6"
                  >
                    OBJEDNAT ({totalPrice.toFixed(2)}€)
                  </Typography>
                </MyButton>
              </Box>
            </Box>
          </Drawer>
        </Box>
      ) : (
        <Box paddingBottom={8}>
          {hasOrdered ? (
            <Box>
              {!orderValid && (
                <Box marginBottom={2}>
                  <MyError
                    title="Neplatna objednavka"
                    desc="Niektore polozky z objednavky uz nie su dostupne. Skontrolujte
              prosim svoju objednavku a skuste to znovu."
                    autoShow={true}
                  ></MyError>
                </Box>
              )}

              <Paper
                elevation={0}
                sx={{
                  borderRadius: 4,
                  bgcolor: picked
                    ? "grey.800"
                    : hungerGame.pizzaLocation
                      ? "success.dark"
                      : theme.palette.primary.main,
                  color: "white",
                  py: 4.5,
                  px: { xs: 2.5, sm: 3.5 },
                  textAlign: "center",
                  mb: 3,
                  boxShadow: "0 12px 32px rgba(0,0,0,0.12)"
                }}
              >
                {picked ? (
                  <>
                    <BeenhereIcon sx={{ fontSize: 52, mb: 1 }} />
                    <Typography variant="h5" fontWeight={800} sx={{ mb: 0.5 }}>
                      Objednavka bola prevzata!
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.88 }}>
                      Prajeme vam dobru chut!
                    </Typography>
                  </>
                ) : hungerGame.pizzaLocation ? (
                  <>
                    <CheckCircleIcon sx={{ fontSize: 52, mb: 1 }} />
                    <Typography variant="h5" fontWeight={800} sx={{ mb: 0.5 }}>
                      Pizza prisla!
                    </Typography>
                    <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5 }}>
                      {hungerGame.pizzaLocation}
                    </Typography>
                  </>
                ) : (
                  <>
                    <RestaurantMenuIcon sx={{ fontSize: 52, mb: 1 }} />
                    <Typography variant="h5" fontWeight={800} sx={{ mb: 0.5 }}>
                      Objednavka bola prijata!
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 1, opacity: 0.92 }}>
                      Pizza pride priblizne{" "}
                    </Typography>
                    <Typography variant="h3" fontWeight={800} sx={{ mt: 0.5 }}>
                      {hungerGame.eta.getDate() !== new Date().getDate() ||
                      hungerGame.eta.getMonth() !== new Date().getMonth() ||
                      hungerGame.eta.getFullYear() !== new Date().getFullYear()
                        ? hungerGame.eta.getMonth() === new Date().getMonth() &&
                          hungerGame.eta.getDate() ===
                            new Date().getDate() + 1 &&
                          hungerGame.eta.getFullYear() ===
                            new Date().getFullYear()
                          ? "zajtra "
                          : hungerGame.eta.getDate() +
                            "." +
                            (hungerGame.eta.getMonth() + 1) +
                            ". " +
                            (hungerGame.eta.getFullYear() !=
                            new Date().getFullYear()
                              ? hungerGame.eta.getFullYear() + " "
                              : "")
                        : ""}
                      {hungerGame.eta.getHours().toString().length < 2
                        ? "0"
                        : ""}
                      {hungerGame.eta.getHours()}:
                      {hungerGame.eta.getMinutes().toString().length < 2
                        ? "0"
                        : ""}
                      {hungerGame.eta.getMinutes()}
                    </Typography>
                  </>
                )}
              </Paper>
              {hungerGame.infoText && (
                <Paper
                  elevation={0}
                  sx={{
                    bgcolor: "grey.800",
                    color: "white",
                    py: 2,
                    px: 3,
                    m: 3
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <InfoIcon />
                    <Typography variant="body2" color="text.primary">
                      {hungerGame.infoText}
                    </Typography>
                  </Box>
                </Paper>
              )}

              <Grid container gap={2} margin="0 auto" justifyContent={"center"}>
                <Grid item xs={12} md={4}>
                  <Paper
                    elevation={1}
                    sx={{ borderRadius: 3, p: 2.5, height: "100%" }}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <PaymentIcon color="primary" />
                      <Typography variant="h6" fontWeight={700}>
                        Platba
                      </Typography>
                    </Box>

                    <Typography variant="h3" fontWeight={700} mb={2}>
                      {totalPrice.toFixed(2)}€
                    </Typography>

                    {hungerGame.iban && (
                      <>
                        <Box mb={1.5}>
                          <Typography variant="body2" color="text.secondary">
                            IBAN
                          </Typography>
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            sx={{ cursor: "pointer" }}
                            onClick={() => {
                              hungerGame.iban &&
                                navigator.clipboard.writeText(hungerGame.iban);
                              alert("IBAN skopirovany do schranky");
                            }}
                            title="Kliknutim skopirujes"
                          >
                            <Typography variant="body1" fontWeight={600}>
                              {hungerGame.iban}
                            </Typography>
                            <ContentCopy
                              sx={{ fontSize: 16, color: "text.secondary" }}
                            />
                          </Box>
                        </Box>

                        {hungerGame.creditorName && (
                          <Box mb={1.5}>
                            <Typography variant="body2" color="text.secondary">
                              Meno prijimatela
                            </Typography>
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={1}
                              sx={{ cursor: "pointer" }}
                              onClick={() => {
                                hungerGame.creditorName &&
                                  navigator.clipboard.writeText(
                                    hungerGame.creditorName
                                  );
                                alert(
                                  "Meno prijimatela skopirovane do schranky"
                                );
                              }}
                              title="Kliknutim skopirujes"
                            >
                              <Typography variant="body1" fontWeight={600}>
                                {hungerGame.creditorName}
                              </Typography>
                              <ContentCopy
                                sx={{ fontSize: 16, color: "text.secondary" }}
                              />
                            </Box>
                          </Box>
                        )}
                      </>
                    )}

                    {qrString && (
                      <Box
                        display="flex"
                        justifyContent="center"
                        bgcolor="white"
                        borderRadius={2}
                        p={1.5}
                        mt={2}
                      >
                        <QRCode value={qrString} size={130} />
                      </Box>
                    )}

                    {paymentLink && (
                      <Button
                        href={paymentLink}
                        rel="noopener noreferrer"
                        variant="contained"
                        target="_blank"
                        fullWidth
                        sx={{
                          backgroundColor: "#05F",
                          color: "white",
                          fontFamily: "Nunito,sans-serif",
                          height: "3rem",
                          mt: 2,
                          "&:hover": {
                            backgroundColor: "#0044cc"
                          }
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          height="1.25rem"
                          viewBox="0 0 112.91 40"
                        >
                          <path
                            d="M15,1.26A8.09,8.09,0,0,1,17.9,4.7a11.85,11.85,0,0,1,1,5.11,11.57,11.57,0,0,1-1,5.06A7.94,7.94,0,0,1,15,18.22a7.86,7.86,0,0,1-4.3,1.18,7.35,7.35,0,0,1-3.53-.84,5.89,5.89,0,0,1-2.4-2.32V24a2,2,0,0,1-.65,1.56,2.49,2.49,0,0,1-1.72.57,2.46,2.46,0,0,1-1.72-.61A2.07,2.07,0,0,1,0,23.92V2.35A2.15,2.15,0,0,1,.63.72,2.34,2.34,0,0,1,2.32.11,2.41,2.41,0,0,1,4,.72a2.13,2.13,0,0,1,.64,1.63V3.3A5.86,5.86,0,0,1,7.1.89,7.39,7.39,0,0,1,10.7,0,7.7,7.7,0,0,1,15,1.26Zm-2,12.91a6.71,6.71,0,0,0,1.26-4.36,7,7,0,0,0-1.26-4.48,4.67,4.67,0,0,0-6.6-.44,5.18,5.18,0,0,0-.41.4A7,7,0,0,0,4.69,9.74a6.92,6.92,0,0,0,1.23,4.41,4.74,4.74,0,0,0,6.7.33h0a3,3,0,0,0,.31-.31ZM36.68,2a8,8,0,0,1,1.88,5.83v9.28a2.12,2.12,0,0,1-.6,1.62,2.35,2.35,0,0,1-1.68.59,2.22,2.22,0,0,1-1.61-.61,2.09,2.09,0,0,1-.63-1.6v-.84a5,5,0,0,1-2,2.32,6.18,6.18,0,0,1-3.22.84,7.47,7.47,0,0,1-3.38-.77A6,6,0,0,1,23,16.53a5.2,5.2,0,0,1-.87-3,4.67,4.67,0,0,1,1-3.23,6.33,6.33,0,0,1,3.4-1.72,30.79,30.79,0,0,1,6.5-.53h1V7.21a3.84,3.84,0,0,0-.8-2.71,3.35,3.35,0,0,0-2.58-.86A8.16,8.16,0,0,0,28.4,4q-1.14.33-2.7.93a3.86,3.86,0,0,1-1.44.5,1.44,1.44,0,0,1-1.13-.5A1.88,1.88,0,0,1,22.7,3.6,2,2,0,0,1,23,2.48a3.09,3.09,0,0,1,1.08-.89A12.48,12.48,0,0,1,27.28.44,16.43,16.43,0,0,1,30.91,0,7.68,7.68,0,0,1,36.68,2ZM32.84,14.69A4.6,4.6,0,0,0,34,11.4v-.8h-.68a25,25,0,0,0-4,.23,3.73,3.73,0,0,0-2,.8,2,2,0,0,0-.61,1.56,2.59,2.59,0,0,0,.86,2,3,3,0,0,0,2.11.78,4.06,4.06,0,0,0,3.09-1.27ZM57.18.48A2,2,0,0,1,58.3.15a2.37,2.37,0,0,1,1.57.59,1.84,1.84,0,0,1,.7,1.47,1.9,1.9,0,0,1-.22.87L50,24.83a2.13,2.13,0,0,1-2,1.28,2.29,2.29,0,0,1-1.54-.55,1.73,1.73,0,0,1-.67-1.38,2.55,2.55,0,0,1,.23-.95l2.59-5.44L41.49,3.07a1.87,1.87,0,0,1-.19-.87A1.84,1.84,0,0,1,42,.72a2.56,2.56,0,0,1,1.7-.6,2.16,2.16,0,0,1,1.18.32,2.19,2.19,0,0,1,.83,1l5.33,11.75L56.36,1.48A2.17,2.17,0,0,1,57.18.48Zm33,1.37a8.66,8.66,0,0,1,1.61,5.83V17a2.13,2.13,0,0,1-.67,1.69,2.53,2.53,0,0,1-1.73.59,2.48,2.48,0,0,1-1.69-.59A2.15,2.15,0,0,1,87,17V7.68a4.78,4.78,0,0,0-.74-3A2.78,2.78,0,0,0,84,3.8a3.71,3.71,0,0,0-3,1.27,5.15,5.15,0,0,0-1.1,3.48V17a2.16,2.16,0,0,1-.67,1.69,2.48,2.48,0,0,1-1.69.59,2.55,2.55,0,0,1-1.71-.59A2.12,2.12,0,0,1,75.18,17V7.68a4.64,4.64,0,0,0-.74-3,2.75,2.75,0,0,0-2.26-.89,3.66,3.66,0,0,0-3,1.27,5.18,5.18,0,0,0-1.08,3.48V17a2.09,2.09,0,0,1-.69,1.69,2.55,2.55,0,0,1-1.71.59,2.48,2.48,0,0,1-1.69-.59A2.13,2.13,0,0,1,63.39,17V2.32A2,2,0,0,1,64.08.68,2.58,2.58,0,0,1,65.79.11,2.42,2.42,0,0,1,67.4.66,2,2,0,0,1,68,2.24v.91A6,6,0,0,1,70.37.83,6.9,6.9,0,0,1,73.74,0a5.44,5.44,0,0,1,5.58,3.5A6.34,6.34,0,0,1,81.8,1,7.19,7.19,0,0,1,85.48,0,5.94,5.94,0,0,1,90.15,1.85Zm22.2,12.64a2.09,2.09,0,0,1,.4,1.33,2.32,2.32,0,0,1-1.41,2A13.25,13.25,0,0,1,108.41,19a12.11,12.11,0,0,1-3.12.44,9.72,9.72,0,0,1-7.11-2.59,9.49,9.49,0,0,1-2.62-7.07A10.94,10.94,0,0,1,96.7,4.68a8.29,8.29,0,0,1,3.22-3.42A9,9,0,0,1,104.62,0,8.36,8.36,0,0,1,109,1.14a7.5,7.5,0,0,1,2.89,3.12,10.41,10.41,0,0,1,1,4.75c0,1.09-.49,1.64-1.45,1.64H100.24a6,6,0,0,0,1.48,3.86,5,5,0,0,0,3.65,1.24,7.13,7.13,0,0,0,2.15-.31,20.14,20.14,0,0,0,2.11-.84,4.2,4.2,0,0,1,1.67-.6A1.26,1.26,0,0,1,112.35,14.49ZM101.63,4.6a5.67,5.67,0,0,0-1.39,3.5h8.59a5.47,5.47,0,0,0-1.14-3.52,3.78,3.78,0,0,0-3-1.2,4.11,4.11,0,0,0-3.1,1.22ZM56.45,40c-9.84,0-19.45-4.75-19.86-5a1.9,1.9,0,0,1,1.7-3.4h0c.09.05,9.24,4.55,18.17,4.55s18.07-4.5,18.17-4.55A1.9,1.9,0,1,1,76.49,35l-.17.08C75.91,35.25,66.3,40,56.45,40Z"
                            fill="#ffffff"
                          />
                        </svg>
                      </Button>
                    )}
                  </Paper>
                </Grid>

                <Grid item xs={12} md={7}>
                  <Paper
                    elevation={1}
                    sx={{ borderRadius: 3, p: 2.5, height: "100%" }}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <ShoppingCartIcon color="primary" />
                      <Typography variant="h6" fontWeight={700}>
                        Tvoja objednavka
                      </Typography>
                    </Box>

                    <Grid container justifyContent={"center"} margin="0 auto">
                      {pizzaOrders
                        .toSorted((a: TPizzaOrder, b: TPizzaOrder) => {
                          return a.pizza.position - b.pizza.position;
                        })
                        .map((order, idx) => {
                          return (
                            <Pizza
                              elevation={2}
                              key={`${order.pizza.id}-${idx}`}
                              pizza={{ ...order.pizza, note: order.note }}
                              count={
                                order.count && order.count > 1
                                  ? order.count
                                  : undefined
                              }
                              textcolor={
                                order.valid ? "text.primary" : "error.main"
                              }
                            />
                          );
                        })}
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
              {canOrder && (
                <Box
                  position={"fixed"}
                  bottom={0}
                  width={"100%"}
                  right={0}
                  padding={2}
                  paddingBottom={0}
                >
                  <MyButton fullWidth onClick={cancelOrder}>
                    <Typography
                      sx={{
                        fontWeight: 700
                      }}
                      variant="h6"
                    >
                      Zrusit objednavku
                    </Typography>
                  </MyButton>
                  {hungerGame && !hungerGame.finished && (
                    <Box
                      display={"flex"}
                      bgcolor={theme.palette.background.default}
                      justifyContent={"center"}
                    >
                      <Typography variant="h6" sx={{ textAlign: "center" }}>
                        {countMin} : {countSec}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          ) : (
            <Box>
              <Typography sx={{ textAlign: "center" }}>
                Nestihol si objednat, mozno nabuduce
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export const OrderedPizzas = ({
  orderedPizzas,
  checkButton,
  onCheck,
  showChecked,
  orderablePizzas,
  showOnlyValid,
  onUserViewClick,
  allUsers,
  editOrder
}: {
  orderedPizzas: Record<string, TPizzaOrder[]>;
  checkButton: boolean;
  onCheck?: (customer: string) => void;
  showChecked: boolean;
  orderablePizzas: TPizza[];
  showOnlyValid: boolean;
  onUserViewClick: (user: TUser) => void;
  allUsers: Record<string, TUser>;
  editOrder?: (customer: string, order: TPizzaOrder) => void;
}) => {
  const [orderEditOpen, setOrderEditOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<string | null>(null);
  const [editingOrder, setEditingOrder] = useState<TPizzaOrder | null>(null);

  const onEditOrderChange = <T extends keyof TPizzaOrder>(
    fieldChanged: T,
    newValue: TPizzaOrder[T]
  ) => {
    if (editingOrder) {
      setEditingOrder({ ...editingOrder, [fieldChanged]: newValue });
    }
  };

  const onEditOrderSave = () => {
    if (editingOrder && editOrder && editingCustomer) {
      editOrder(editingCustomer, editingOrder);
      setOrderEditOpen(false);
    } else {
      alert("Nie je vybrana objednavka na ulozenie");
    }
  };

  const picked_customers: string[] = Object.keys(orderedPizzas).filter(
    (cust: string) =>
      orderedPizzas[cust].filter((pizza: TPizzaOrder) => pizza.picked).length >
      0
  );
  const customers: string[] = Object.keys(orderedPizzas).filter(
    (cust: string) => !picked_customers.includes(cust)
  );

  const isInOrderableList = (pizza: TPizza) => {
    return (
      orderablePizzas.filter(ordPizza => ordPizza.id === pizza.id).length > 0
    );
  };

  const all_custs: string[] = [];
  customers.forEach((customer: string) => all_custs.push(customer));
  picked_customers.forEach((customer: string) => all_custs.push(customer));
  return (
    <>
      {all_custs.map(customer => {
        let user: TUser | undefined = undefined;
        let pu: TPartialUser | undefined = undefined;
        try {
          pu = JSON.parse(customer);
          user = pu && allUsers[pu.uid];
        } catch (err) {
          user = undefined;
        }
        const isChecked =
          orderedPizzas[customer].filter((ord: TPizzaOrder) => ord.picked)
            .length > 0;
        const areAllInvalid =
          orderedPizzas[customer].filter((ord: TPizzaOrder) => ord.valid)
            .length == 0;
        if ((showChecked || !isChecked) && (!showOnlyValid || !areAllInvalid)) {
          return (
            <Box
              key={customer}
              padding={3}
              paddingY={1.5}
              width={"450px"}
              sx={{ margin: "0 auto" }}
            >
              <Paper elevation={1}>
                <Box
                  padding={2}
                  bgcolor={isChecked ? "grey" : theme.palette.primary.main}
                  borderRadius={4}
                >
                  <Grid container>
                    <Grid item xs={9}>
                      <Typography variant="h5" color={"black"}>
                        {user ? (
                          <>
                            {userToString(user)}{" "}
                            <IconButton
                              sx={{
                                marginTop: -1,
                                color: "black"
                              }}
                              size="small"
                              onClick={() => {
                                if (user) {
                                  onUserViewClick(user);
                                }
                              }}
                            >
                              <AccountCircle />
                            </IconButton>
                          </>
                        ) : (
                          customer.replace(
                            /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/,
                            ""
                          )
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Box display={"flex"} justifyContent={"end"}>
                        <Typography variant="h5" color={"black"}>
                          {orderedPizzas[customer]
                            .reduce((acc, pizza) => {
                              const count = pizza.count ? pizza.count : 1;
                              return (
                                acc +
                                count * (pizza.customPrice ?? pizza.pizza.price)
                              );
                            }, 0)
                            .toFixed(2)}
                          €
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={8}>
                      <Box component="div">
                        {orderedPizzas[customer].map((pizza, index) => {
                          return (
                            (!showOnlyValid || pizza.valid) && (
                              <Pizza
                                key={`${pizza.pizza.id}-${pizza.note ?? ""}-${pizza.count ?? 1}-${index}`}
                                textcolor={
                                  isInOrderableList(pizza.pizza)
                                    ? "text.primary"
                                    : "red"
                                }
                                pizza={{
                                  ...pizza.pizza,
                                  note: pizza.note
                                }}
                                customPrice={pizza.customPrice}
                                editPizza={
                                  editOrder
                                    ? () => {
                                        setEditingOrder(pizza);
                                        setEditingCustomer(customer);
                                        setOrderEditOpen(true);
                                      }
                                    : undefined
                                }
                                count={pizza.count}
                                showPosition={true}
                              ></Pizza>
                            )
                          );
                        })}
                      </Box>
                    </Grid>
                  </Grid>
                  {checkButton && onCheck && (
                    <MyButton
                      color={isChecked ? "primary" : "inherit"}
                      onClick={() => {
                        onCheck(customer);
                      }}
                    >
                      <CheckIcon />
                    </MyButton>
                  )}
                </Box>
              </Paper>
            </Box>
          );
        }
      })}
      <Drawer //Order edit
        anchor={"bottom"}
        open={orderEditOpen && editOrder != null}
        onClose={() => {
          setOrderEditOpen(false);
        }}
      >
        <Box height={"80vh"}>
          {editingOrder && (
            <OrderForm
              order={editingOrder}
              onChange={onEditOrderChange}
              onSave={onEditOrderSave}
            />
          )}
        </Box>
      </Drawer>
    </>
  );
};

export const OrderForm = ({
  order,
  onChange,
  onSave
}: {
  order: TPizzaOrder;
  onChange: <T extends keyof TPizzaOrder>(
    fieldChanged: T,
    newValue: TPizzaOrder[T]
  ) => void;
  onSave: () => void;
}) => {
  return (
    <>
      <Box padding={3} marginBottom={5}>
        <FormGroup>
          <TextField
            sx={{ margin: 1 }}
            value={order.customPrice ?? ""}
            placeholder={order.pizza.price.toString()}
            onChange={e =>
              onChange("customPrice", parseFloat(e.target.value) || undefined)
            }
            label="Custom price"
            variant="standard"
            fullWidth
            type="number"
          />
        </FormGroup>
      </Box>
      <Box position={"fixed"} bottom={20} width={"100%"} paddingX={2}>
        <MyButton fullWidth onClick={onSave}>
          <Typography
            sx={{
              fontWeight: 700
            }}
            variant="h6"
          >
            ULOZIT
          </Typography>
        </MyButton>
      </Box>
    </>
  );
};

export const CustomersOrders = ({
  autoReload,
  setAutoReload,
  showOnlyValid,
  setShowOnlyValid,
  orderedPizzas,
  orderablePizzas,
  checkButton,
  onPizzaCheck,
  onScriptOpen,
  onUserViewClick,
  validPizzasCount,
  allPizzasCount,
  allUsers,
  editOrder
}: {
  autoReload: boolean;
  setAutoReload: (value: boolean) => void;
  showOnlyValid: boolean;
  setShowOnlyValid: (value: boolean) => void;
  orderedPizzas: Record<string, TPizzaOrder[]>;
  orderablePizzas: TPizza[];
  checkButton: boolean;
  onPizzaCheck?: (customer: string) => void;
  onUserViewClick: (user: TUser) => void;
  onScriptOpen?: () => void;
  validPizzasCount?: number;
  allPizzasCount?: number;
  allUsers: Record<string, TUser>;
  editOrder?: (customer: string, order: TPizzaOrder) => void;
}) => {
  const [showChecked, setShowChecked] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchedPizzas, setSearchedPizzas] = useState<
    Record<string, TPizzaOrder[]>
  >({});

  const customersToSearchStrings = useMemo(() => {
    const map: Record<string, string> = {};
    Object.keys(orderedPizzas).forEach(customer => {
      try {
        const pu = JSON.parse(customer) as TPartialUser | undefined;
        const user = pu && allUsers[pu.uid];
        map[customer] = user
          ? [
              user.first_name,
              user.last_name,
              user.username,
              user.email,
              user.phone
            ]
              .join(" ")
              .toLowerCase()
          : customer.toLowerCase();
      } catch (err) {
        map[customer] = customer.toLowerCase();
      }
    });
    return map;
  }, [orderedPizzas, allUsers]);

  const search = (query: string) => {
    const q = query.toLowerCase();
    const customers = Object.keys(orderedPizzas).filter(customer => {
      const customerMatch = customersToSearchStrings[customer].includes(q);
      const pizzaMatch =
        orderedPizzas[customer].filter(order => {
          return (
            order.pizza.name.toLowerCase().includes(q) ||
            order.note?.toLowerCase().includes(q) ||
            order.pizza.ingredients.toLowerCase().includes(q) ||
            order.pizza.position.toString().toLowerCase().includes(q)
          );
        }).length > 0;
      return customerMatch || pizzaMatch;
    });
    const results: Record<string, TPizzaOrder[]> = {};
    customers.forEach(customer => {
      results[customer] = orderedPizzas[customer];
    });
    return results;
  };

  useEffect(() => {
    const results = searchQuery ? search(searchQuery) : {};
    setSearchedPizzas(results);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, orderedPizzas, allUsers]);

  return (
    <Grid
      container
      justifyContent={"center"}
      maxWidth={"1200px"}
      margin="0 auto"
    >
      <Box
        display={"flex"}
        justifyContent={"center"}
        flexWrap={"wrap"}
        style={{ width: "100%" }}
        paddingX={0}
        marginX={4}
        marginBottom={1}
      >
        {onScriptOpen && (
          <MyButton
            disabled={Object.keys(orderedPizzas).length == 0}
            style={{ marginTop: 8, marginRight: 20 }}
            color={"inherit"}
            onClick={onScriptOpen}
            title="Zoznam"
          >
            <Badge
              badgeContent={showOnlyValid ? validPizzasCount : allPizzasCount}
              color="primary"
            >
              <Description />
            </Badge>
          </MyButton>
        )}
        <MyButton
          style={{ marginTop: 8, marginRight: 20 }}
          color={autoReload ? "primary" : "inherit"}
          onClick={() => setAutoReload(!autoReload)}
          title="Auto reload"
        >
          <CachedIcon />
        </MyButton>
        <MyButton
          style={{ marginTop: 8, marginRight: 20 }}
          color={!showChecked ? "primary" : "inherit"}
          onClick={() => setShowChecked(!showChecked)}
          title={showChecked ? "Showing all" : "Showing only not picked"}
        >
          <BeenhereIcon />
        </MyButton>
        <MyButton
          style={{ marginTop: 8, marginRight: 20 }}
          color={showOnlyValid ? "primary" : "inherit"}
          onClick={() => setShowOnlyValid(!showOnlyValid)}
          title="Show only allowed (pizzas that are in menu)"
        >
          <RestaurantMenuIcon />
        </MyButton>
        <TextField
          value={searchQuery}
          onChange={e => {
            setSearchQuery(e.target.value);
          }}
          sx={{ flexGrow: 1 }}
          label="Search"
          helperText="Search customers, pizzas (name, ingredients), notes, positions"
          variant="standard"
          // style={{ width: "100%" }}
          // fullWidth
        />
      </Box>
      {Object.keys(orderedPizzas).length > 0 ? (
        <>
          {searchQuery.length != 0 ? (
            <>
              {Object.keys(searchedPizzas).length > 0 ? (
                <OrderedPizzas
                  orderedPizzas={searchedPizzas}
                  orderablePizzas={orderablePizzas}
                  checkButton={checkButton}
                  onCheck={onPizzaCheck}
                  showChecked={showChecked}
                  showOnlyValid={showOnlyValid}
                  onUserViewClick={onUserViewClick}
                  allUsers={allUsers}
                  editOrder={editOrder}
                />
              ) : (
                <Box display={"flex"} justifyContent={"center"}>
                  <Typography variant="h4" sx={{ textAlign: "center" }}>
                    Nic som nenasiel
                  </Typography>
                </Box>
              )}
            </>
          ) : (
            <OrderedPizzas
              orderablePizzas={orderablePizzas}
              orderedPizzas={orderedPizzas}
              checkButton={checkButton}
              onCheck={onPizzaCheck}
              showChecked={showChecked}
              showOnlyValid={showOnlyValid}
              onUserViewClick={onUserViewClick}
              allUsers={allUsers}
              editOrder={editOrder}
            />
          )}
        </>
      ) : (
        <Box display={"flex"} justifyContent={"center"}>
          <Typography variant="h4" sx={{ textAlign: "center", marginTop: 3 }}>
            Zatial ziadne objednavky
          </Typography>
        </Box>
      )}
    </Grid>
  );
};
